import React from 'react';

import { Button, Group, LoadingOverlay, Modal, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import WorkoutRichTextEditor from 'components/workout-program/WorkoutRichTextEditor';
import useCreateWorkoutBenchmark from 'hooks/workoutBenchmark/useCreateWorkoutBenchmark';
import useUpdateWorkoutBenchmark from 'hooks/workoutBenchmark/useUpdateWorkoutBenchmark';
import useWorkoutBenchmark from 'hooks/workoutBenchmark/useWorkoutBenchmark';
import { WorkoutMovementWrite } from 'types';
import { MovementTypeEnum, SCORE_TYPE_OPTIONS, ScoreTypeEnum } from 'types/enums';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

type DefaultScoreType = WorkoutMovementWrite['defaultScoreType'];

interface FormValues {
  name: string;
  description: string;
  defaultScoreType: ScoreTypeEnum | null;
  workoutMovementId: string;
}

const normalizeDefaultScoreType = (value: DefaultScoreType): ScoreTypeEnum | null => {
  if (!value) return null;
  return value as ScoreTypeEnum;
};

const isHtmlEmpty = (html: string): boolean => html.replace(/<[^>]*>/g, '').trim() === '';

export interface CreatedBenchmark {
  id: string;
  name: string;
  defaultScoreType: ScoreTypeEnum | null;
  description: string | null;
}

interface IModalBenchmarkFormProps {
  opened: boolean;
  closeHandler: () => void;
  benchmarkId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
  /** Fired after a successful create — lets callers (e.g. the builder) link the new benchmark. */
  onCreated?: (benchmark: CreatedBenchmark) => void;
}

const ModalBenchmarkForm = ({
  opened,
  closeHandler,
  benchmarkId,
  type,
  permission = true,
  onCreated,
}: IModalBenchmarkFormProps): JSX.Element => {
  const createMutation = useCreateWorkoutBenchmark();
  const updateMutation = useUpdateWorkoutBenchmark();

  const { data: benchmarkData, isLoading: isLoadingBenchmark } = useWorkoutBenchmark(benchmarkId);
  const benchmark = benchmarkData?.data;

  // Default benchmarks: name + description are read-only, scoring type stays editable.
  const detailsLocked = type === 'edit' && !!benchmark?.isDefault;

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      description: '',
      defaultScoreType: null,
      workoutMovementId: uuidv4(),
    },
    validate: {
      name: value => {
        if (!value || value.trim() === '') return 'Benchmark name is required';
        if (value.length > 255) return 'Benchmark name must be 255 characters or less';
        return null;
      },
      description: value => (isHtmlEmpty(value) ? 'Description is required' : null),
    },
  });

  const { successNotification, errorNotification } = notificationHandler();

  useShallowEffect(() => {
    if (type === 'edit' && benchmark) {
      form.setValues({
        name: benchmark.name,
        description: benchmark.description ?? '',
        defaultScoreType: normalizeDefaultScoreType(benchmark.defaultScoreType),
        workoutMovementId: benchmark.workoutMovementId,
      });
    } else if (type === 'create') {
      form.reset();
      form.setFieldValue('workoutMovementId', uuidv4());
    }
  }, [type, benchmark, opened]);

  const handleCloseAndReset = (): void => {
    form.reset();
    closeHandler();
  };

  const handleSubmit = (values: FormValues): void => {
    const payload: WorkoutMovementWrite = {
      workoutMovementId: values.workoutMovementId,
      name: values.name.trim(),
      description: values.description,
      defaultScoreType: values.defaultScoreType,
      movementType: MovementTypeEnum.BENCHMARK,
    };

    if (type === 'create') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          successNotification('Benchmark created', `${values.name} has been created successfully.`);
          onCreated?.({
            id: payload.workoutMovementId,
            name: payload.name,
            defaultScoreType: values.defaultScoreType,
            description: values.description || null,
          });
          handleCloseAndReset();
        },
        onError: error => {
          const errorMessage = error.response?.data?.message || 'Failed to create benchmark';
          errorNotification('Creation failed', errorMessage);
        },
      });
    } else {
      updateMutation.mutate(
        {
          benchmarkId: benchmarkId!,
          data: payload,
        },
        {
          onSuccess: () => {
            successNotification('Benchmark updated', `${values.name} has been updated successfully.`);
            handleCloseAndReset();
          },
          onError: error => {
            const errorMessage = error.response?.data?.message || 'Failed to update benchmark';
            errorNotification('Update failed', errorMessage);
          },
        },
      );
    }
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>{type === 'create' ? 'New Benchmark' : 'Edit Benchmark'}</Title>}
      size='lg'
    >
      <LoadingOverlay visible={isLoadingBenchmark} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='md'>
          {detailsLocked && (
            <Text size='xs' color='dimmed'>
              This is a default benchmark — its name and description can&apos;t be edited. You can still
              change the scoring type.
            </Text>
          )}
          <TextInput
            label='Name'
            placeholder='Enter benchmark name'
            required
            disabled={!permission || detailsLocked}
            {...form.getInputProps('name')}
            data-cy='benchmarkNameInput'
          />
          <WorkoutRichTextEditor
            label='Description'
            value={form.values.description}
            onChange={html => form.setFieldValue('description', html)}
            editable={permission && !detailsLocked}
            error={form.errors.description}
            dataCy='benchmarkDescriptionInput'
          />
          <Select
            label='Scoring Type'
            placeholder='Optional — select a scoring type'
            data={SCORE_TYPE_OPTIONS}
            disabled={!permission}
            clearable
            {...form.getInputProps('defaultScoreType')}
            data-cy='benchmarkScoreTypeInput'
          />

          <Group position='right' mt='md'>
            <Button
              variant='subtle'
              onClick={handleCloseAndReset}
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              loading={createMutation.isLoading || updateMutation.isLoading}
              disabled={!permission}
            >
              {type === 'create' ? 'Create' : 'Save Changes'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalBenchmarkForm;
